import React from "react";
import type {
  ShopEntity,
  ShopHeaderDesign,
} from "../../../../../../domain/types/company-shop";

interface PreviewHeaderProps {
  companyLogoUrl: string | null;
  defaultLogoUrl: string;
  headerDesign: ShopHeaderDesign;
  shopEntityData: ShopEntity | null;
  /**
   * shops.portfolio_enabled. ShopEntity 에는 없고 포트폴리오 설정 API 에서
   * 따로 읽어온 값이다. 아직 못 읽었으면 null.
   */
  portfolioMenuEnabled: boolean | null;
}

/**
 * 메뉴의 visible 은 기존 노출 조건과 AND 로 결합한다.
 *
 * PORTFOLIO 가 보는 값은 shops.portfolio_enabled 다 — 메인 섹션의
 * on/off(shop_sections.is_enabled)가 아니다. 둘은 일부러 분리돼 있어서,
 * 메뉴에는 두되 메인에는 안 띄우는 조합이 가능하다.
 * 아직 못 읽었으면(null) 감추지 않는다. 있는 메뉴를 잠깐 숨겼다 되살리는
 * 것보다, 값이 도착할 때까지 그대로 두는 편이 덜 흔들린다.
 *
 * CONSULT 는 MallDesign 이 상담 ON/OFF 와 실시간 상담 플러그인으로 다시 계산한
 * consultChannelType 을 본다.
 */
const isMenuItemRenderable = (
  key: string,
  shopEntityData: ShopEntity | null,
  portfolioMenuEnabled: boolean | null,
): boolean => {
  if (key === "CONSULT") {
    return (shopEntityData?.consultChannelType ?? "NONE") !== "NONE";
  }
  if (key === "PORTFOLIO") {
    return portfolioMenuEnabled ?? true;
  }
  return true;
};

/** 미리보기 상단 헤더 (로고 + 유저/장바구니 아이콘 + 메뉴) */
export const PreviewHeader = ({
  companyLogoUrl,
  defaultLogoUrl,
  headerDesign,
  shopEntityData,
  portfolioMenuEnabled,
}: PreviewHeaderProps) => {
  const visibleMenuItems = [...headerDesign.menuItems]
    .filter((item) => item.visible)
    .filter((item) =>
      isMenuItemRenderable(item.key, shopEntityData, portfolioMenuEnabled),
    )
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const isCenterAligned = headerDesign.logoAlignment === "CENTER";

  return (
    <div
      className={`px-[32px] py-[16px] font-medium ${
        headerDesign.stickyEnabled ? "sticky top-0 z-40" : ""
      }`}
      style={{
        backgroundColor: headerDesign.backgroundColor,
        color: headerDesign.textColor,
      }}
    >
      <div className="flex flex-row justify-between items-start">
        {isCenterAligned ? <div className="w-[68px] shrink-0" /> : null}

        {companyLogoUrl === null ? (
          <div className="w-2" />
        ) : (
          <img
            src={companyLogoUrl || defaultLogoUrl}
            alt="Company Logo"
            className={`mb-[1rem] max-w-[120px] max-h-[30px] ${
              isCenterAligned ? "" : "-translate-x-1"
            }`}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = defaultLogoUrl;
            }}
          />
        )}

        <div className="flex flex-row gap-[1.5rem] items-center">
          <div className="relative">
            <img
              className="w-[1.25rem]"
              alt="유저 아이콘"
              src="/assets/icons/Profile.svg"
            />
          </div>
          <div className="relative">
            {/*
              쇼핑몰 header.ejs 에는 장바구니 개수 배지가 아예 없다(아이콘 링크뿐).
              즉 이 빨간 배지와 "3" 은 미리보기에만 있는 장식이고 대응하는
              쇼핑몰 색도 없다. 색만 바꿔도 실제와 맞출 수 없어서 F-10 에서는
              그대로 뒀다 — 지울지 말지는 제품 결정이다.
            */}
            <div className="absolute bottom-2 left-4 w-[1rem] h-[1rem] bg-[#FB2C36] rounded-full text-white text-[0.625rem]">
              3
            </div>
            <img
              className="w-[1.25rem]"
              alt="쇼핑 카트 아이콘"
              src="/assets/icons/Buy.svg"
            />
          </div>
        </div>
      </div>

      <div
        className={`flex flex-row flex-wrap text-[0.875rem] gap-[2rem] ${
          isCenterAligned ? "justify-center" : ""
        }`}
      >
        {visibleMenuItems.map((item) => (
          <div key={item.key}>{item.label}</div>
        ))}
      </div>
    </div>
  );
};
